import { type ComponentContainer, type ComponentItem, GoldenLayout, LayoutConfig, type ResolvedComponentItemConfig, RowOrColumn } from "golden-layout";
import { CodeEditor } from "./ui/code-editor";
import { FloatingPanelManager, type FloatingPanelState } from "./ui/floating-panels";
import AssetsWindow from "./windows/assets";
import CodeEditorWindow from "./windows/code-editor-window";
import HierarchyWindow from "./windows/hierarchy";
import InspectorWindow from "./windows/inspector";
import ViewportWindow from "./windows/viewport";
import GameWindow from "./windows/game";
import TerminalWindow from "./windows/terminal-window";
import LogWindow from "./windows/log-window";
import { TerminalProvider } from "./ui/terminal-provider";
import { renderWindowControls } from "./ui/window-controls";
import { activeWindowService, editorAssetStore, editorSelectionService, setPopoutWindowFlag } from "./ui/window-services";
import type { EditorWindow, SpawnWindowOptions, WindowDefinition, WindowManagerApi, WindowType } from "./ui/window-framework";
import type { PopoutComponentConfig } from "./cross-window";
import { getCrossWindowChannel } from "./cross-window";
import Metadata from "@flint/shared/metadata";
import GameObject from "@flint/runtime/game-object";
import Layer from "@flint/runtime/layer";
import { System } from "@flint/runtime/system";

const STORAGE_KEY = "flint.editor.layout";
const HOST_ID = "layout-host";
const STORAGE_VERSION = 8;
const HEADER_HEIGHT = 20;

let currentLayout: GoldenLayout | null = null;
let currentFloatingPanels: FloatingPanelManager | null = null;
let editorWindowRegistry: EditorWindowRegistry | null = null;
const knownWindowStacks = new Set<LayoutStackItem>();
const windowStacksByInstanceId = new Map<string, LayoutStackItem>();

type LiveResizeRowOrColumn = {
    _dimension: "width" | "height";
    _splitterPosition: number | null;
    _splitterMinPosition: number | null;
    _splitterMaxPosition: number | null;
    _liveResizeBeforeSize: number | undefined;
    _liveResizeAfterSize: number | undefined;
    getSplitItems: (splitter: { element: HTMLElement }) => {
        before: MinSizeContentItem & { element: HTMLElement; size: number; updateSize: (force: boolean) => void };
        after: MinSizeContentItem & { element: HTMLElement; size: number; updateSize: (force: boolean) => void };
    };
    calculateContentItemMinSize: (contentItem: MinSizeContentItem) => number;
    updateSize: (force: boolean) => void;
};

type MinSizeContentItem = {
    readonly isRow: boolean;
    readonly isColumn: boolean;
    readonly isStack: boolean;
    readonly contentItems: MinSizeContentItem[];
};
function minExtentAlongAxis(
    item: MinSizeContentItem,
    splitAlongColumn: boolean,
    leafMinSize: (leaf: MinSizeContentItem) => number
): number {
    const children = item.contentItems;
    if (item.isRow || item.isColumn) {
        if (children.length === 0) {
            return leafMinSize(item);
        }
        const extents = children.map(child => minExtentAlongAxis(child, splitAlongColumn, leafMinSize));
        if (item.isColumn === splitAlongColumn) {
            return extents.reduce((total, extent) => total + extent, 0);
        }
        return Math.max(...extents);
    }
    if (item.isStack) {
        if (children.length === 0) {
            return leafMinSize(item);
        }
        return Math.max(...children.map(child => minExtentAlongAxis(child, splitAlongColumn, leafMinSize)));
    }
    return leafMinSize(item);
}

type AnimatedDropTargetIndicator = {
    _element?: HTMLElement;
    highlightArea: (area: { x1: number; y1: number; x2: number; y2: number }, margin: number) => void;
    hide: () => void;
    __flintAnimationInstalled?: boolean;
    __flintHideTimeout?: number;
};

type StoredWindowComponentState = {
    windowType: WindowType;
    instanceId: string;
    windowState?: Record<string, unknown>;
    title?: string;
};

type StoredLayoutSnapshot = {
    version: number;
    dockedLayout: LayoutConfig;
    floatingPanels: FloatingPanelState[];
};

type WindowInstanceRecord = {
    window: EditorWindow;
    container: ComponentContainer;
    type: WindowType;
};

type LayoutComponentItem = {
    componentType?: string;
    parent?: LayoutStackItem;
    container?: ComponentContainer;
    element?: HTMLElement;
};

type LayoutStackItem = {
    isStack?: boolean;
    header?: { controlsContainerElement?: HTMLElement };
    on?: (event: string, callback: () => void) => void;
    getActiveContentItem?: () => LayoutComponentItem | null | undefined;
    __flintWindowControlsBound?: boolean;
};

const windowDefinitions: readonly WindowDefinition[] = [
    { type: "Viewport", title: "Viewport", create: context => new ViewportWindow(context) },
    { type: "Game", title: "Game", create: context => new GameWindow(context) },
    { type: "CodeEditor", title: "Code Editor", create: context => new CodeEditorWindow(context) },
    { type: "Hierarchy", title: "Hierarchy", create: context => new HierarchyWindow(context) },
    { type: "Assets", title: "Assets", create: context => new AssetsWindow(context) },
    { type: "Inspector", title: "Inspector", create: context => new InspectorWindow(context) },
    { type: "Terminal", title: "Terminal", create: context => new TerminalWindow(context) },
    { type: "Log", title: "Log", create: context => new LogWindow(context) }
] as const;

class EditorWindowRegistry implements WindowManagerApi {
    private readonly definitions = new Map<WindowType, WindowDefinition>();
    private readonly instances = new Map<string, WindowInstanceRecord>();
    private instanceCounter = 0;
    private refreshTimeouts = new Map<WindowType | "all", number>();

    public constructor(public readonly layout: GoldenLayout) {
        for (const definition of windowDefinitions) {
            this.definitions.set(definition.type, definition);
        }
    }

    public getWindow(instanceId: string): EditorWindow | undefined {
        return this.instances.get(instanceId)?.window;
    }

    public refreshWindows(type?: WindowType): void {
        const key = type ?? "all";
        if (this.refreshTimeouts.has(key)) {
            return;
        }

        this.refreshTimeouts.set(key, window.setTimeout(() => {
            this.refreshTimeouts.delete(key);
            for (const record of this.instances.values()) {
                if (type && record.type !== type) {
                    continue;
                }
                record.window.update?.();
            }
        }, 50));
    }

    public refreshWindowControls(instanceId: string): void {
        const record = this.instances.get(instanceId);
        if (!record) {
            return;
        }

        const stack = windowStacksByInstanceId.get(instanceId);
        if (stack) {
            syncWindowControlsForStack(stack);
        } else {
            syncWindowControls();
        }
        currentFloatingPanels?.refreshWindowControls(instanceId);
    }

    public createWindow(container: ComponentContainer, itemConfig: ResolvedComponentItemConfig): HTMLElement {
        const state = this.resolveWindowState(itemConfig);
        const definition = this.definitions.get(state.windowType);
        if (!definition) {
            throw new Error(`Unknown window type "${state.windowType}".`);
        }

        const root = document.createElement("div");
        root.dataset.windowType = state.windowType;
        root.dataset.instanceId = state.instanceId;

        const editorWindow = definition.create({
            instanceId: state.instanceId,
            type: state.windowType,
            root,
            container,
            services: {
                selection: editorSelectionService,
                assets: editorAssetStore,
                activeWindows: activeWindowService
            },
            manager: this
        });

        this.instances.set(state.instanceId, {
            window: editorWindow,
            container,
            type: state.windowType
        });

        container.stateRequestEvent = () => ({
            windowType: state.windowType,
            instanceId: state.instanceId,
            windowState: editorWindow.serializeState(),
            title: container.title
        });

        container.element.appendChild(root);
        root.addEventListener("mousedown", () => this.activateWindow(state.instanceId));
        root.addEventListener("focusin", () => this.activateWindow(state.instanceId));

        editorWindow.restoreState(state.windowState);
        Promise.resolve(editorWindow.initialize()).then(() => {
            this.activateWindow(state.instanceId);
        });

        return root;
    }

    public destroyWindow(container: ComponentContainer): void {
        const instanceId = (container.stateRequestEvent?.() as Partial<StoredWindowComponentState> | undefined)?.instanceId
            ?? container.element.firstElementChild?.getAttribute("data-instance-id");
        if (!instanceId) {
            return;
        }

        const record = this.instances.get(instanceId);
        if (!record) {
            return;
        }

        activeWindowService.removeWindow(instanceId);
        windowStacksByInstanceId.delete(instanceId);
        record.window.dispose();
        this.instances.delete(instanceId);
    }

    public activateWindow(instanceId: string): void {
        const record = this.instances.get(instanceId);
        if (!record) {
            return;
        }

        activeWindowService.setActiveWindow(record.type, instanceId);
        record.window.onActivate?.();
    }

    public spawnWindow(type: WindowType, options?: SpawnWindowOptions): string {
        const definition = this.definitions.get(type);
        if (!definition) {
            throw new Error(`Unknown window type "${type}".`);
        }

        const instanceId = this.createInstanceId(type);
        const title = options?.title ?? definition.title;
        const componentState: StoredWindowComponentState = {
            windowType: type,
            instanceId,
            title,
            ...(options?.state !== undefined ? { windowState: options.state } : {})
        };
        const componentConfig = {
            type: "component",
            componentType: type,
            componentState,
            title,
            isClosable: true,
            reorderEnabled: true
        };

        if (currentFloatingPanels) {
            currentFloatingPanels.spawnFloatingComponent(componentConfig);
        } else {
            this.layout.addComponent(type, componentState, title);
        }

        return instanceId;
    }

    public getWindowsOfType<T extends EditorWindow>(type: WindowType): T[] {
        const result: T[] = [];
        for (const record of this.instances.values()) {
            if (record.type === type) {
                result.push(record.window as T);
            }
        }
        return result;
    }

    public getRecordsOfType(type: WindowType): WindowInstanceRecord[] {
        const result: WindowInstanceRecord[] = [];
        for (const record of this.instances.values()) {
            if (record.type === type) {
                result.push(record);
            }
        }
        return result;
    }

    private resolveWindowState(itemConfig: ResolvedComponentItemConfig): StoredWindowComponentState {
        const rawState = itemConfig.componentState as Partial<StoredWindowComponentState> | undefined;
        const windowType = (rawState?.windowType ?? itemConfig.componentType) as WindowType;
        const instanceId = typeof rawState?.instanceId === "string"
            ? rawState.instanceId
            : this.createInstanceId(windowType);

        const windowStateValue = rawState?.windowState;
        return {
            windowType,
            instanceId,
            ...(windowStateValue !== undefined ? { windowState: windowStateValue } : {}),
            title: typeof rawState?.title === "string" ? rawState.title : itemConfig.title
        };
    }

    private createInstanceId(type: WindowType): string {
        this.instanceCounter += 1;
        return `flint-${type.toLowerCase()}-${this.instanceCounter}`;
    }
}

function installLiveSplitterResize() {
    const prototype = RowOrColumn.prototype as unknown as {
        onSplitterDragStart: (this: LiveResizeRowOrColumn, splitter: { element: HTMLElement }) => void;
        onSplitterDrag: (this: LiveResizeRowOrColumn, splitter: { element: HTMLElement }, offsetX: number, offsetY: number) => void;
        onSplitterDragStop: (this: LiveResizeRowOrColumn, splitter: { element: HTMLElement }) => void;
        __flintLiveResizeInstalled?: boolean;
    };

    if (prototype.__flintLiveResizeInstalled) {
        return;
    }

    prototype.__flintLiveResizeInstalled = true;

    const originalDragStart = prototype.onSplitterDragStart;
    const originalDrag = prototype.onSplitterDrag;
    const originalDragStop = prototype.onSplitterDragStop;

    prototype.onSplitterDragStart = function (splitter) {
        originalDragStart.call(this, splitter);
        const items = this.getSplitItems(splitter);
        this._liveResizeBeforeSize = Number.parseFloat(items.before.element.style[this._dimension]) || 0;
        this._liveResizeAfterSize = Number.parseFloat(items.after.element.style[this._dimension]) || 0;

        if (this._liveResizeBeforeSize !== undefined && this._liveResizeAfterSize !== undefined) {
            const splitAlongColumn = this._dimension === "height";
            const leafMinSize = (leaf: MinSizeContentItem) => this.calculateContentItemMinSize(leaf);
            const beforeMinSize = minExtentAlongAxis(items.before, splitAlongColumn, leafMinSize);
            const afterMinSize = minExtentAlongAxis(items.after, splitAlongColumn, leafMinSize);
            this._splitterMinPosition = -1 * (this._liveResizeBeforeSize - beforeMinSize);
            this._splitterMaxPosition = this._liveResizeAfterSize - afterMinSize;
        }
    };

    prototype.onSplitterDrag = function (splitter, offsetX, offsetY) {
        originalDrag.call(this, splitter, offsetX, offsetY);
        if (this._splitterPosition === null || this._liveResizeBeforeSize === undefined || this._liveResizeAfterSize === undefined) {
            return;
        }

        const items = this.getSplitItems(splitter);
        const beforeSize = this._liveResizeBeforeSize + this._splitterPosition;
        const afterSize = this._liveResizeAfterSize - this._splitterPosition;
        const sizeProp = this._dimension;

        items.before.element.style[sizeProp] = `${beforeSize}px`;
        items.after.element.style[sizeProp] = `${afterSize}px`;
        splitter.element.style.top = "0px";
        splitter.element.style.left = "0px";
        items.before.updateSize(false);
        items.after.updateSize(false);
    };

    prototype.onSplitterDragStop = function (splitter) {
        if (this._splitterPosition !== null && this._liveResizeBeforeSize !== undefined && this._liveResizeAfterSize !== undefined) {
            const items = this.getSplitItems(splitter);
            const finalBeforeSize = this._liveResizeBeforeSize + this._splitterPosition;
            const totalSize = this._liveResizeBeforeSize + this._liveResizeAfterSize;
            const splitterPositionInRange = finalBeforeSize / totalSize;
            const totalRelativeSize = items.before.size + items.after.size;

            items.before.size = splitterPositionInRange * totalRelativeSize;
            items.after.size = (1 - splitterPositionInRange) * totalRelativeSize;
            splitter.element.style.top = "0px";
            splitter.element.style.left = "0px";
            this._liveResizeBeforeSize = undefined;
            this._liveResizeAfterSize = undefined;
            globalThis.requestAnimationFrame(() => this.updateSize(false));
            return;
        }

        originalDragStop.call(this, splitter);
    };
}

function installAnimatedDropTargetIndicator(layout: GoldenLayout) {
    const internalLayout = layout as GoldenLayout & {
        dropTargetIndicator?: AnimatedDropTargetIndicator | null;
    };

    const indicator = internalLayout.dropTargetIndicator;
    if (!indicator || indicator.__flintAnimationInstalled) {
        return;
    }

    const element = indicator._element;
    if (!(element instanceof HTMLElement)) {
        return;
    }

    indicator.__flintAnimationInstalled = true;
    const originalHighlightArea = indicator.highlightArea.bind(indicator);

    indicator.highlightArea = (area, margin) => {
        window.clearTimeout(indicator.__flintHideTimeout);
        element.classList.remove("flint-drop-target-fading");
        originalHighlightArea(area, margin);
        element.classList.add("flint-drop-target-visible");
    };

    indicator.hide = () => {
        window.clearTimeout(indicator.__flintHideTimeout);
        element.classList.remove("flint-drop-target-visible");
        element.classList.add("flint-drop-target-fading");
        indicator.__flintHideTimeout = window.setTimeout(() => {
            element.classList.remove("flint-drop-target-fading");
            element.style.display = "none";
        }, 140);
    };
}

function getInstanceIdFromContainer(container: ComponentContainer | undefined): string | null {
    const state = container?.stateRequestEvent?.() as Partial<StoredWindowComponentState> | undefined;
    if (typeof state?.instanceId === "string") {
        return state.instanceId;
    }

    const root = container?.element.firstElementChild;
    return root instanceof HTMLElement ? root.dataset.instanceId ?? null : null;
}

function getInstanceIdFromItem(item: LayoutComponentItem | null | undefined): string | null {
    const containerInstanceId = getInstanceIdFromContainer(item?.container);
    if (containerInstanceId) {
        return containerInstanceId;
    }

    const root = item?.element?.querySelector<HTMLElement>("[data-instance-id]");
    return root?.dataset.instanceId ?? null;
}

function getWindowFromItem(item: LayoutComponentItem | null | undefined): EditorWindow | null {
    const instanceId = getInstanceIdFromItem(item);
    if (!instanceId || !editorWindowRegistry) {
        return null;
    }

    return editorWindowRegistry.getWindow(instanceId) ?? null;
}

function getWindowControlsFromItem(item: LayoutComponentItem | null | undefined) {
    return getWindowFromItem(item)?.getControls?.() ?? [];
}

function syncWindowControlsForStack(stack: LayoutStackItem | undefined): void {
    if (!stack?.isStack) {
        return;
    }

    knownWindowStacks.add(stack);

    const controlsHost = stack.header?.controlsContainerElement;
    if (!controlsHost) {
        return;
    }

    if (!stack.__flintWindowControlsBound) {
        stack.on?.("activeContentItemChanged", () => syncWindowControlsForStack(stack));
        stack.__flintWindowControlsBound = true;
    }

    const activeItem = stack.getActiveContentItem?.();
    const activeInstanceId = getInstanceIdFromItem(activeItem);
    if (activeInstanceId) {
        windowStacksByInstanceId.set(activeInstanceId, stack);
    }

    renderWindowControls(controlsHost, getWindowControlsFromItem(activeItem), {
        placement: "prepend",
        extraClassName: "lm_controls_button"
    });
}

function syncWindowControls(): void {
    if (!editorWindowRegistry) {
        return;
    }

    const stacks = new Set<LayoutStackItem>();
    for (const type of ["Game", "Viewport", "CodeEditor", "Hierarchy", "Assets", "Inspector", "Terminal", "Log"] as const) {
        for (const record of editorWindowRegistry.getRecordsOfType(type)) {
            const stack = (record.container as ComponentContainer & { parent?: LayoutStackItem }).parent;
            if (stack) {
                stacks.add(stack);
            }
        }
    }
    for (const stack of knownWindowStacks) {
        stacks.add(stack);
    }

    for (const stack of stacks) {
        syncWindowControlsForStack(stack);
    }
}

function createPanelStack(type: WindowType, title: string, size: string) {
    return {
        type: "stack" as const,
        size,
        isClosable: true,
        content: [
            {
                type: "component" as const,
                componentType: type,
                componentState: {
                    windowType: type
                },
                title,
                isClosable: true,
                reorderEnabled: true
            }
        ]
    };
}

function createViewportAndEditorStack() {
    return {
        type: "stack" as const,
        size: "75%",
        isClosable: true,
        content: [
            {
                type: "component" as const,
                componentType: "Game",
                componentState: { windowType: "Game" },
                title: "Game",
                isClosable: true,
                reorderEnabled: true
            },
            {
                type: "component" as const,
                componentType: "Viewport",
                componentState: { windowType: "Viewport" },
                title: "Viewport",
                isClosable: true,
                reorderEnabled: true
            },
            {
                type: "component" as const,
                componentType: "CodeEditor",
                componentState: { windowType: "CodeEditor" },
                title: "Code Editor",
                isClosable: true,
                reorderEnabled: true
            }
        ]
    };
}

function createTerminalLogStack() {
    return {
        type: "stack" as const,
        size: "50%",
        isClosable: true,
        content: [
            {
                type: "component" as const,
                componentType: "Terminal",
                componentState: { windowType: "Terminal" },
                title: "Terminal",
                isClosable: true,
                reorderEnabled: true
            },
            {
                type: "component" as const,
                componentType: "Log",
                componentState: { windowType: "Log" },
                title: "Log",
                isClosable: true,
                reorderEnabled: true
            }
        ]
    };
}

function createDefaultLayout(): LayoutConfig {
    return {
        root: {
            type: "row",
            content: [
                {
                    type: "column",
                    size: "80%",
                    content: [
                        {
                            type: "row",
                            size: "80%",
                            content: [
                                createViewportAndEditorStack(),
                                createPanelStack("Hierarchy", "Hierarchy", "20%")
                            ]
                        },
                        {
                            type: "row",
                            size: "25%",
                            content: [
                                createPanelStack("Assets", "Assets", "50%"),
                                createTerminalLogStack()
                            ]
                        }
                    ]
                },
                createPanelStack("Inspector", "Inspector", "20%")
            ]
        },
        settings: {
            reorderEnabled: true,
            popoutWholeStack: false,
            showPopoutIcon: false,
            showMaximiseIcon: true,
            showCloseIcon: true
        },
        dimensions: {
            defaultMinItemHeight: "180px",
            defaultMinItemWidth: "220px",
            headerHeight: HEADER_HEIGHT,
            borderWidth: 5
        },
        header: {
            show: "top",
            popout: false,
            maximise: "max",
            close: "Close",
            tabDropdown: "more"
        }
    };
}

function bindPanelComponent(container: ComponentContainer, itemConfig: ResolvedComponentItemConfig) {
    if (!editorWindowRegistry) {
        throw new Error("Editor window registry is not initialized.");
    }

    const root = editorWindowRegistry.createWindow(container, itemConfig);
    return {
        component: {
            rootHtmlElement: root
        },
        virtual: false
    };
}

function unbindPanelComponent(container: ComponentContainer) {
    editorWindowRegistry?.destroyWindow(container);
    const panelRoot = container.element.firstElementChild;
    if (panelRoot instanceof HTMLElement) {
        panelRoot.remove();
    }
}

function loadStoredLayout(): StoredLayoutSnapshot | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as Partial<StoredLayoutSnapshot> & LayoutConfig;
        if ("dockedLayout" in parsed) {
            return {
                version: typeof parsed.version === "number" ? parsed.version : STORAGE_VERSION,
                dockedLayout: parsed.dockedLayout ?? createDefaultLayout(),
                floatingPanels: Array.isArray(parsed.floatingPanels) ? parsed.floatingPanels : []
            };
        }

        return {
            version: STORAGE_VERSION,
            dockedLayout: parsed as LayoutConfig,
            floatingPanels: []
        };
    } catch {
        localStorage.removeItem(STORAGE_KEY);
        return null;
    }
}

function makeItemDockable(item: unknown, parentType?: string): unknown {
    if (!item || typeof item !== "object") {
        return item;
    }

    const typedItem = item as {
        type?: string;
        content?: unknown[];
        componentType?: WindowType;
        componentState?: Record<string, unknown>;
        isClosable?: boolean;
        header?: Record<string, unknown>;
    };

    if (typedItem.type === "component" && parentType !== "stack") {
        return {
            type: "stack" as const,
            isClosable: true,
            content: [typedItem]
        };
    }

    if (typedItem.type === "stack") {
        return {
            ...typedItem,
            isClosable: true,
            header: {
                ...typedItem.header,
                close: "Close"
            },
            content: typedItem.content?.map(child => makeItemDockable(child, "stack"))
        };
    }

    if ((typedItem.type === "row" || typedItem.type === "column") && typedItem.content) {
        return {
            ...typedItem,
            content: typedItem.content.map(child => makeItemDockable(child, typedItem.type))
        };
    }

    return typedItem;
}

function normalizeLayoutConfig(layoutConfig: LayoutConfig): LayoutConfig {
    return {
        ...layoutConfig,
        root: layoutConfig.root ? makeItemDockable(layoutConfig.root) as LayoutConfig["root"] : layoutConfig.root,
        settings: {
            ...layoutConfig.settings,
            reorderEnabled: true,
            popoutWholeStack: false,
            showPopoutIcon: false,
            showMaximiseIcon: true,
            showCloseIcon: true
        },
        dimensions: {
            ...layoutConfig.dimensions,
            headerHeight: HEADER_HEIGHT
        },
        header: {
            ...layoutConfig.header,
            show: "top",
            popout: false,
            maximise: "max",
            close: "Close",
            tabDropdown: "more"
        }
    };
}

function saveLayout(layout: GoldenLayout) {
    const resolved = layout.saveLayout();
    const serializable = LayoutConfig.fromResolved(resolved);
    const snapshot: StoredLayoutSnapshot = {
        version: STORAGE_VERSION,
        dockedLayout: serializable,
        floatingPanels: currentFloatingPanels?.serialize() ?? []
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
}

export function resetStoredLayout(): void {
    localStorage.removeItem(STORAGE_KEY);
}

export function resetEditorLayout(): void {
    const layout = currentLayout;
    if (!layout) {
        resetStoredLayout();
        return;
    }

    resetStoredLayout();
    currentFloatingPanels?.clearFloatingPanels(true);
    layout.loadLayout(createDefaultLayout());
}

export function initializeEditorLayout(): GoldenLayout {
    installLiveSplitterResize();

    const host = document.getElementById(HOST_ID);
    if (!host || !(host instanceof HTMLElement)) {
        throw new Error(`Layout host "${HOST_ID}" was not found.`);
    }

    const layout = new GoldenLayout(host, bindPanelComponent, unbindPanelComponent);
    currentLayout = layout;
    editorWindowRegistry = new EditorWindowRegistry(layout);
    CodeEditor.setWindowSpawner(type => editorWindowRegistry!.spawnWindow(type));
    TerminalProvider.setWindowSpawner(type => editorWindowRegistry!.spawnWindow(type));

    (window as any).__flint = { System, Metadata, GameObject, Layer };

    layout.setSize(host.clientWidth || 1, host.clientHeight || 1);
    layout.resizeWithContainerAutomatically = true;

    let saveTimeout: number | undefined;
    currentFloatingPanels = new FloatingPanelManager(layout, host, () => {
        window.clearTimeout(saveTimeout);
        saveTimeout = window.setTimeout(() => saveLayout(layout), 100);
    }, componentItem => getWindowControlsFromItem(componentItem as LayoutComponentItem),
        handlePopoutComponent);

    (layout as unknown as { on: (event: string, callback: (item: unknown) => void) => void }).on("itemCreated", (event: unknown) => {
        const item = (event as { _target?: unknown })._target as LayoutComponentItem | undefined;
        syncWindowControlsForStack(item?.parent);
    });

    layout.addEventListener("stateChanged", () => {
        window.clearTimeout(saveTimeout);
        saveTimeout = window.setTimeout(() => saveLayout(layout), 100);
        syncWindowControls();
    });

    const storedSnapshot = loadStoredLayout();
    const layoutConfig = normalizeLayoutConfig(storedSnapshot?.dockedLayout ?? createDefaultLayout());

    try {
        layout.loadLayout(layoutConfig);
        installAnimatedDropTargetIndicator(layout);
        currentFloatingPanels.restore(storedSnapshot?.floatingPanels ?? []);
        syncWindowControls();
    } catch (error) {
        console.warn("Failed to load stored GoldenLayout config, restoring defaults.", error);
        localStorage.removeItem(STORAGE_KEY);
        layout.loadLayout(createDefaultLayout());
        installAnimatedDropTargetIndicator(layout);
        syncWindowControls();
    }

    return layout;
}

function handlePopoutComponent(componentItem: ComponentItem): void {
    const state = (componentItem as LayoutComponentItem).container?.stateRequestEvent?.() as StoredWindowComponentState | undefined;
    if (!state) {
        return;
    }

    const config: PopoutComponentConfig = {
        componentType: state.windowType,
        instanceId: state.instanceId,
        title: state.title ?? componentItem.title
    };
    if (state.windowState !== undefined) {
        config.windowState = state.windowState;
    }

    const encoded = encodeURIComponent(JSON.stringify(config));
    const url = `${window.location.origin}${window.location.pathname}?gl_popout=${encoded}`;
    const bounds = getScreenBoundsForPopout(componentItem);
    const availLeft = (window.screen as unknown as { availLeft?: number })?.availLeft ?? 0;
    const availTop = (window.screen as unknown as { availTop?: number })?.availTop ?? 0;
    const availWidth = window.screen.availWidth || window.screen.width;
    const availHeight = window.screen.availHeight || window.screen.height;
    const clampedLeft = Math.max(availLeft, Math.min(bounds.left, availLeft + availWidth - bounds.width));
    const clampedTop = Math.max(availTop, Math.min(bounds.top, availTop + availHeight - bounds.height));
    const features = `popup,left=${clampedLeft},screenX=${clampedLeft},top=${clampedTop},screenY=${clampedTop},width=${bounds.width},height=${bounds.height}`;
    window.open(url, `flint-popout-${config.instanceId}`, features);
}

function getPopoutSourceRect(componentItem: ComponentItem): DOMRect {
    const floatingShell = (componentItem.element as HTMLElement).closest?.(".flint-floating-window") as HTMLElement | null;
    if (floatingShell) {
        return floatingShell.getBoundingClientRect();
    }

    const stack = (componentItem as LayoutComponentItem).parent;
    const stackElement = (stack as unknown as { element?: HTMLElement })?.element;
    if (stack?.isStack && stackElement) {
        const contentItems = (stack as unknown as { contentItems?: unknown[] })?.contentItems;
        if (Array.isArray(contentItems) && contentItems.length === 1) {
            return stackElement.getBoundingClientRect();
        }
    }

    return componentItem.element.getBoundingClientRect();
}

function getScreenBoundsForPopout(componentItem: ComponentItem): { left: number; top: number; width: number; height: number; rawRect: DOMRect } {
    const rect = getPopoutSourceRect(componentItem);
    const screenLeft = (window.screenLeft as number | undefined) ?? window.screenX;
    const screenTop = (window.screenTop as number | undefined) ?? window.screenY;
    const frameBorderX = Math.max(0, (window.outerWidth - window.innerWidth) / 2);
    const rawFrameChromeY = Math.max(0, window.outerHeight - window.innerHeight - frameBorderX);
    const frameChromeY = Math.min(140, rawFrameChromeY);

    return {
        left: Math.round(screenLeft + frameBorderX + rect.left),
        top: Math.round(screenTop + frameChromeY + rect.top),
        width: Math.max(220, Math.round(rect.width)),
        height: Math.max(180, Math.round(rect.height)),
        rawRect: rect
    };
}

function createPopoutLayoutConfig(type: WindowType, instanceId: string, title?: string): LayoutConfig {
    return {
        root: {
            type: "stack",
            content: [
                {
                    type: "component",
                    componentType: type,
                    componentState: {
                        windowType: type,
                        instanceId,
                        title
                    },
                    title: title ?? type,
                    isClosable: false,
                    reorderEnabled: false
                }
            ]
        },
        settings: {
            reorderEnabled: false,
            popoutWholeStack: false,
            showPopoutIcon: false,
            showMaximiseIcon: false,
            showCloseIcon: false
        },
        dimensions: {
            defaultMinItemHeight: "180px",
            defaultMinItemWidth: "220px",
            headerHeight: 0,
            borderWidth: 0
        },
        header: {
            show: false,
            popout: false
        }
    };
}

export function initializePopoutWindow(config: PopoutComponentConfig): void {
    setPopoutWindowFlag();

    const opener = (window.opener as any)?.__flint;
    if (opener) {
        (System as any)._world = opener.System.world;
        System.components = opener.System.components;
        System.fileSystem = opener.System.fileSystem;
        (Metadata as any).classMeta = opener.Metadata.classMeta;
        (Metadata as any).fieldMeta = opener.Metadata.fieldMeta;
    }

    const channel = getCrossWindowChannel();
    channel.send({ type: "POPOUT_HANDSHAKE", source: "popout", config });

    installLiveSplitterResize();

    const host = document.getElementById(HOST_ID);
    if (!host || !(host instanceof HTMLElement)) {
        throw new Error(`Layout host "${HOST_ID}" was not found.`);
    }

    const popoutManager: WindowManagerApi = {
        layout: null as unknown as GoldenLayout,
        activateWindow: (instanceId: string) => {
            activeWindowService.setActiveWindow(config.componentType as WindowType, instanceId);
        },
        refreshWindows: () => { },
        refreshWindowControls: () => { },
        spawnWindow: () => { throw new Error("Cannot spawn windows in popout"); }
    };

    const layout = new GoldenLayout(host, (container, itemConfig) => {
        const root = document.createElement("div");
        root.dataset.windowType = config.componentType;
        root.dataset.instanceId = config.instanceId;

        const definition = windowDefinitions.find(d => d.type === config.componentType as WindowType);
        if (!definition) {
            throw new Error(`Unknown window type "${config.componentType}".`);
        }

        const editorWindow = definition.create({
            instanceId: config.instanceId,
            type: config.componentType as WindowType,
            root,
            container,
            services: {
                selection: editorSelectionService,
                assets: editorAssetStore,
                activeWindows: activeWindowService
            },
            manager: popoutManager
        });

        container.stateRequestEvent = () => ({
            windowType: config.componentType,
            instanceId: config.instanceId,
            windowState: editorWindow.serializeState(),
            title: config.title ?? container.title
        });

        container.element.appendChild(root);
        root.addEventListener("mousedown", () => editorWindow.onActivate?.());
        root.addEventListener("focusin", () => editorWindow.onActivate?.());
        editorWindow.restoreState(config.windowState);
        Promise.resolve(editorWindow.initialize()).then(() => {
            editorWindow.onActivate?.();
        });

        return {
            component: { rootHtmlElement: root },
            virtual: false
        };
    }, () => { });

    layout.loadLayout(createPopoutLayoutConfig(
        config.componentType as WindowType,
        config.instanceId,
        config.title
    ));

    layout.setSize(host.clientWidth || 1, host.clientHeight || 1);
    layout.resizeWithContainerAutomatically = true;
}

export function spawnEditorWindow(type: WindowType, options?: SpawnWindowOptions): string {
    if (!editorWindowRegistry) {
        throw new Error("Editor window registry is not initialized.");
    }

    return editorWindowRegistry.spawnWindow(type, options);
}

export function refreshEditorWindows(type?: WindowType): void {
    editorWindowRegistry?.refreshWindows(type);
}

export function getEditorWindowsOfType<T extends EditorWindow>(type: WindowType): T[] {
    return editorWindowRegistry?.getWindowsOfType<T>(type) ?? [];
}
